/**
 * AI Provider options for backend QnA service
 */
export enum AIProvider {
  OpenAI = 'openai',
  Google = 'google'
}

/**
 * Backend QnA API parameters
 * These are sent directly to POST /v1/qna/ask when override flags are enabled
 */
export interface BackendQnAParams {
  provider?: AIProvider;
  model?: string;
  maxTokens?: number;
  temperature?: number;
  documentId?: string;
}

/**
 * Frontend AI Service Configuration
 * Extends backend parameters with UI control flags
 */
export interface AIServiceConfig extends BackendQnAParams {
  maxContextMessages: number;
  // Backend override flags (control whether to send parameter to backend)
  overrideProvider?: boolean;
  overrideModel?: boolean;
  overrideMaxTokens?: boolean;
  overrideTemperature?: boolean;
  overrideDocumentId?: boolean;
}

export interface AIServiceConfigValidation {
  maxContextMessages: {
    isValid: boolean;
    error?: string;
  };
  provider?: {
    isValid: boolean;
    error?: string;
  };
  model?: {
    isValid: boolean;
    error?: string;
  };
  maxTokens?: {
    isValid: boolean;
    error?: string;
  };
  temperature?: {
    isValid: boolean;
    error?: string;
  };
  documentId?: {
    isValid: boolean;
    error?: string;
  };
}

export interface ConfigurationModalState {
  isOpen: boolean;
  activeTab: 'backend-override' | 'advanced';
  config: AIServiceConfig;
  validation: AIServiceConfigValidation;
  isValidating: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
}

export interface IConfigurationService {
  getConfig(): Promise<AIServiceConfig>;
  setConfig(config: Partial<AIServiceConfig>): Promise<boolean>;
  resetToDefaults(): Promise<void>;
  exportConfig(): Promise<string>;
  parseImportConfig(configJson: string): Promise<AIServiceConfig | null>;
  importConfig(configJson: string): Promise<boolean>;
}

export interface IStorageService {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<boolean>;
  remove(key: string): Promise<boolean>;
  clear(): Promise<boolean>;
}

export interface IValidationService {
  validateMaxContextMessages(maxMessages: number): { isValid: boolean; error?: string };
  validateProvider(provider: string): { isValid: boolean; error?: string };
  validateModel(model: string): { isValid: boolean; error?: string };
  validateMaxTokens(maxTokens: number): { isValid: boolean; error?: string };
  validateTemperature(temperature: number): { isValid: boolean; error?: string };
  validateDocumentId(documentId: string): { isValid: boolean; error?: string };
}

export const DEFAULT_SYSTEM_PROMPT = `🖥SYSTEM:
You are the Dorian FAQ Assistant.
Answer strictly using only the information in the ###FAQ### block.
Never invent new facts and never omit technical syntax (e.g. #CHOICE:, #->, #premium).
Translate your reply into the language of the user from the last message

Response format (Markdown):
1. Concise takeaway - one sentence that directly answers the question.
2. Step-by-step guide - numbered list, each line a clear action or fact.
3. Extra details & tips - bullet list starting with "-".
4. Related questions - 2-3 bullets suggesting logical follow-ups.

If the standard format doesn't fit the answer's nature, use a more appropriate structure that ensures clear and complete understanding for the user.

If the FAQ lacks the answer, reply exactly:
"The FAQ does not yet cover this question."

Maintain complete coverage, consistent order, and full detail.

###FAQ###
{context}
###CHAT###`;

export const QNA_MAX_CONTEXT_MESSAGES = 200;

export const DEFAULT_AI_CONFIG: AIServiceConfig = {
  // This section is for user interface and history purposes only. The backend has its own settings for the maximum number of LLM messages sent.
  maxContextMessages: QNA_MAX_CONTEXT_MESSAGES,
  // Backend override flags default to false (use backend defaults)
  overrideProvider: false,
  overrideModel: false,
  overrideMaxTokens: false,
  overrideTemperature: false,
  overrideDocumentId: false,
};

export const CONFIG_STORAGE_KEY = 'qna-widget-config';
